/** @jsx jsx */

import { Card, jsx, Text } from "theme-ui";
import React, { useCallback, useContext } from "react";

import { ActionButton, Stack } from "office-ui-fabric-react";
import PropTypes from "prop-types";
import EditAttachment from "./EditAttachment";
import FormContext from "./FormContext";
import { useLang } from "./LanguageSelector";

import { shade4, shade0, shade5, good, danger } from "./json/colors.json";
import { IkonButton } from "../StyledButtons";
import { STAY_CHECKEDIN, STAY_CHECKEDOUT } from "./constants";

function RenderPersonVisit({ record, adminFields }) {
  const { uid, setP2, admin } = useContext(FormContext);
  const [getLangString] = useLang();

  const locked =
    record.stay_status === STAY_CHECKEDIN ||
    record.stay_status === STAY_CHECKEDOUT;

  const openFormCPassport = useCallback(() => {
    setP2({ record, sections: ["passport"] });
  }, [setP2, record]);
  const openFormCVisa = useCallback(() => {
    setP2({ record, sections: ["visa"] });
  }, [setP2, record]);
  const openFormCTravel = useCallback(() => {
    setP2({ record, sections: ["travel"] });
  }, [setP2, record]);
  const openFormCContact = useCallback(() => {
    setP2({ record, sections: ["contact"] });
  }, [setP2, record]);

  const openPrintDetails = useCallback(() => {
    setP2({
      record,
      sections: ["passport", "visa", "travel", "contact"],
      printOnly: true,
    });
  }, [setP2, record]);

  const { full_name, id, passport_num, formc } = record;
  const {
    applicant_visano,
    applicant_contactnoperm,
    applicant_arrivedfromplace,
  } = formc || {};

  const passportCheck = !!passport_num;
  const visaCheck = !!applicant_visano;
  const travelCheck = !!applicant_arrivedfromplace;
  const contactCheck = !!applicant_contactnoperm;

  if (admin) {
    const rx = {
      pdetail: (
        <IkonButton
          sx={{ color: shade0, backgroundColor: passportCheck ? good : danger }}
          iconProps={{ iconName: passportCheck ? "Accept" : "Warning12" }}
          title={getLangString(
            passportCheck ? "strings.edit_passport" : "strings.add_passport"
          )}
          onClick={openFormCPassport}
        />
      ),
      vdetail: (
        <IkonButton
          sx={{ color: shade0, backgroundColor: visaCheck ? good : danger }}
          iconProps={{ iconName: visaCheck ? "Accept" : "Warning12" }}
          title={getLangString(
            visaCheck ? "strings.edit_visa" : "strings.add_visa"
          )}
          onClick={openFormCVisa}
        />
      ),
      tdetail: (
        <IkonButton
          sx={{ color: shade0, backgroundColor: travelCheck ? good : danger }}
          iconProps={{ iconName: travelCheck ? "Accept" : "Warning12" }}
          title={getLangString(
            travelCheck ? "strings.edit_travel" : "strings.add_travel"
          )}
          onClick={openFormCTravel}
        />
      ),
      cdetail: (
        <IkonButton
          sx={{ color: shade0, backgroundColor: contactCheck ? good : danger }}
          iconProps={{ iconName: contactCheck ? "Accept" : "Warning12" }}
          title={getLangString(
            contactCheck ? "strings.edit_contact" : "strings.add_contact"
          )}
          onClick={openFormCContact}
        />
      ),

      ...Object.fromEntries(
        ["photo", "passport", "visa"].map((x) => {
          // const label_str = `strings.label_${x}`;
          const key = `attach_${x}`;

          return [
            x,
            <EditAttachment
              uploadBtnText={getLangString("strings.upload_file")}
              uid={uid}
              dbKey={key}
              docId={id}
              dbField={
                !!record && key in record && !!record[key]
                  ? record[key]
                  : undefined
              }
              strClick={
                x === "photo"
                  ? getLangString("strings.select_photo")
                  : getLangString(
                      x === "passport"
                        ? "strings.select_passport"
                        : "strings.select_visa"
                    )
              }
            />,
          ];
        })
      ),
    };
    return (
      <React.Fragment>
        {adminFields.map((x) => (
          <td>{x in rx && rx[x]}</td>
        ))}
      </React.Fragment>
    );
  }

  return (
    <Card
      sx={{
        m: 2,
        my: 3,
        p: 2,
        border: "solid 1px ",
        borderColor: shade4,
        backgroundColor: "white",
      }}
    >
      <Stack horizontal horizontalAlign="space-between">
        <Text variant="title">
          {full_name} {locked && getLangString("strings.stay_commenced")}{" "}
        </Text>
        <ActionButton
          sx={{ color: shade4, backgroundColor: "transparent" }}
          text={getLangString("strings.print_details")}
          iconProps={{ iconName: "Print" }}
          onClick={openPrintDetails}
        />
      </Stack>
      <Stack horizontal tokens={{ childrenGap: 10 }} sx={{ my: 2 }}>
        <Stack.Item>
          <Text sx={{ fontSize: "0.8em" }}>
            {getLangString("strings.formc")}
          </Text>
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal tokens={{ childrenGap: 5 }}>
            <Stack tokens={{ childrenGap: 5 }}>
              <ActionButton
                sx={{ color: shade0, backgroundColor: shade5 }}
                iconProps={{ iconName: passportCheck ? "Accept" : "Warning12" }}
                text={getLangString(
                  passportCheck
                    ? "strings.edit_passport"
                    : "strings.add_passport"
                )}
                title={getLangString(
                  passportCheck
                    ? "strings.edit_passport"
                    : "strings.add_passport"
                )}
                onClick={openFormCPassport}
              />

              <ActionButton
                sx={{ color: shade0, backgroundColor: shade5 }}
                iconProps={{ iconName: visaCheck ? "Accept" : "Warning12" }}
                text={getLangString(
                  visaCheck ? "strings.edit_visa" : "strings.add_visa"
                )}
                title={getLangString(
                  visaCheck ? "strings.edit_visa" : "strings.add_visa"
                )}
                onClick={openFormCVisa}
              />
            </Stack>
            <Stack tokens={{ childrenGap: 5 }}>
              <ActionButton
                sx={{ color: shade0, backgroundColor: shade5 }}
                iconProps={{ iconName: travelCheck ? "Accept" : "Warning12" }}
                text={getLangString(
                  travelCheck ? "strings.edit_travel" : "strings.add_travel"
                )}
                title={getLangString(
                  travelCheck ? "strings.edit_travel" : "strings.add_travel"
                )}
                onClick={openFormCTravel}
              />
              <ActionButton
                sx={{ color: shade0, backgroundColor: shade5 }}
                iconProps={{ iconName: contactCheck ? "Accept" : "Warning12" }}
                text={getLangString(
                  contactCheck ? "strings.edit_contact" : "strings.add_contact"
                )}
                title={getLangString(
                  contactCheck ? "strings.edit_contact" : "strings.add_contact"
                )}
                onClick={openFormCContact}
              />
            </Stack>
          </Stack>
        </Stack.Item>
      </Stack>

      <Stack horizontal horizontalAlign="space-between">
        {["photo", "passport", "visa"].map((x) => {
          const label_str = `strings.label_${x}`;
          const key = `attach_${x}`;

          return (
            <Stack.Item>
              <div sx={{ fontSize: "0.8em" }}>{getLangString(label_str)}</div>
              <EditAttachment
                uploadBtnText={getLangString("strings.upload_file")}
                uid={uid}
                dbKey={key}
                docId={id}
                dbField={
                  !!record && key in record && !!record[key]
                    ? record[key]
                    : undefined
                }
                strClick={
                  x === "photo"
                    ? getLangString("strings.select_photo")
                    : getLangString(
                        x === "passport"
                          ? "strings.select_passport"
                          : "strings.select_visa"
                      )
                }
                locked={locked}
              />
            </Stack.Item>
          );
        })}
      </Stack>
    </Card>
  );
}

RenderPersonVisit.propTypes = {
  record: PropTypes.shape({
    full_name: PropTypes.string,
    id: PropTypes.string,
    passport_num: PropTypes.string,
    stay_status: PropTypes.string,
    formc: PropTypes.shape({}),
  }).isRequired,
  adminFields: PropTypes.arrayOf(PropTypes.string),
};
RenderPersonVisit.defaultProps = {
  adminFields: [],
};

export default RenderPersonVisit;
